/**
 * CallWidget - Scheda Chiamata Nativa
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 *
 * READ MODE: Mostra la scheda chiamata come in un telefono (durata, stato, note)
 * CREATE MODE: Form per registrare una nuova chiamata
 */

import { useState, useRef } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  PhoneOff,
  Search,
  Clock,
  Calendar,
  MessageSquare,
  ListTodo,
  Play,
  Pause,
  User,
  Edit3,
  Trash2,
} from 'lucide-react';
import { useHubStore } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks';
import {
  PhoneField,
  TextAreaField,
  Button,
  formStyles as sharedFormStyles,
} from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

interface CallWidgetProps {
  isOpen?: boolean;
  callId?: string;
  call?: Call;
  onClose: () => void;
  onSave?: (data: CallFormData) => void;
  onCallback?: (call: Call) => void;
  onCreateTask?: (call: Call) => void;
  onSendMessage?: (call: Call) => void;
  onDelete?: (callId: string) => void;
}

export interface Call {
  id: string;
  contactId?: string;
  contactName?: string;
  contactAvatar?: string;
  phone?: string;
  direction: 'in' | 'out';
  duration?: number; // seconds
  at: string;
  answered: boolean;
  notes?: string;
  recordingUrl?: string;
}

export interface CallFormData {
  contactId?: string;
  contactName?: string;
  phone?: string;
  direction: 'in' | 'out';
  duration?: number;
  at: string;
  notes?: string;
  answered: boolean;
}

// =============================================================================
// VIEW MODE COMPONENT - Phone Call Card Style
// =============================================================================

function CallViewMode({
  call,
  onClose,
  onCallback,
  onCreateTask,
  onSendMessage,
  onDelete,
}: {
  call: Call;
  onClose: () => void;
  onCallback?: (call: Call) => void;
  onCreateTask?: (call: Call) => void;
  onSendMessage?: (call: Call) => void;
  onDelete?: (callId: string) => void;
}) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    const timeStr = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    if (date.toDateString() === now.toDateString()) {
      return t('calls.todayAt', { time: timeStr });
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return t('calls.yesterdayAt', { time: timeStr });
    }
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  const getCallIcon = () => {
    if (!call.answered) {
      return call.direction === 'in' ? (
        <PhoneMissed size={24} style={{ color: '#ef4444' }} />
      ) : (
        <PhoneOff size={24} style={{ color: '#f59e0b' }} />
      );
    }
    return call.direction === 'in' ? (
      <PhoneIncoming size={24} style={{ color: '#22c55e' }} />
    ) : (
      <PhoneOutgoing size={24} style={{ color: '#3b82f6' }} />
    );
  };

  const getCallStatus = () => {
    if (!call.answered) {
      return call.direction === 'in' ? t('calls.missedCall') : t('calls.notAnswered');
    }
    return call.direction === 'in' ? t('calls.incomingCall') : t('calls.outgoingCall');
  };

  const getStatusColor = () => {
    if (!call.answered) return '#ef4444';
    return call.direction === 'in' ? '#22c55e' : '#3b82f6';
  };

  return (
    <DetailPanel
      title={call.contactName || call.phone || t('calls.call')}
      subtitle={getCallStatus()}
      icon={getCallIcon()}
      onClose={onClose}
      accentColor={getStatusColor()}
      width="50%"
    >
      {/* Contact Card */}
      <div style={viewStyles.contactCard}>
        <div style={viewStyles.avatar}>
          {call.contactAvatar ? (
            <img
              src={call.contactAvatar}
              alt=""
              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            getInitials(call.contactName)
          )}
        </div>
        <div style={viewStyles.contactInfo}>
          <div style={viewStyles.contactName}>{call.contactName || t('calls.unknownNumber')}</div>
          {call.phone && <div style={viewStyles.contactPhone}>{call.phone}</div>}
        </div>
        <div
          style={{
            ...viewStyles.directionBadge,
            background: getStatusColor(),
          }}
        >
          {getCallIcon()}
        </div>
      </div>

      {/* Call Stats */}
      <div style={viewStyles.statsGrid}>
        {/* Duration */}
        <div style={viewStyles.statCard}>
          <div style={viewStyles.statIcon}>
            <Clock size={20} style={{ color: '#6b7280' }} />
          </div>
          <div style={viewStyles.statContent}>
            <div style={viewStyles.statLabel}>{t('calls.duration')}</div>
            <div
              style={{
                ...viewStyles.statValue,
                color: call.answered ? '#1f2937' : '#9ca3af',
              }}
            >
              {call.answered ? formatDuration(call.duration) : '--:--'}
            </div>
          </div>
        </div>

        {/* Date/Time */}
        <div style={viewStyles.statCard}>
          <div style={viewStyles.statIcon}>
            <Calendar size={20} style={{ color: '#6b7280' }} />
          </div>
          <div style={viewStyles.statContent}>
            <div style={viewStyles.statLabel}>{t('calls.dateTime')}</div>
            <div style={viewStyles.statValue}>{formatDate(call.at)}</div>
          </div>
        </div>
      </div>

      {/* Status Indicator */}
      <div
        style={{
          ...viewStyles.statusBanner,
          background: call.answered
            ? call.direction === 'in'
              ? '#dcfce7'
              : '#dbeafe'
            : '#fee2e2',
          borderColor: call.answered
            ? call.direction === 'in'
              ? '#22c55e'
              : '#3b82f6'
            : '#ef4444',
        }}
      >
        <div style={viewStyles.statusIcon}>{getCallIcon()}</div>
        <div style={viewStyles.statusText}>
          <strong>{getCallStatus()}</strong>
          {call.answered && call.duration && (
            <span style={{ marginLeft: '8px', color: '#6b7280' }}>
              • {t('calls.durationLabel', { duration: formatDuration(call.duration) })}
            </span>
          )}
        </div>
      </div>

      {/* Recording (if available) */}
      {call.recordingUrl && (
        <DetailSection title={t('calls.recording')}>
          <div style={viewStyles.recordingPlayer}>
            <button
              onClick={() => setIsPlayingRecording(!isPlayingRecording)}
              style={viewStyles.playBtn}
            >
              {isPlayingRecording ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <div style={viewStyles.waveform}>
              {/* Simplified waveform visualization */}
              {Array.from({ length: 30 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: '3px',
                    height: `${Math.random() * 20 + 8}px`,
                    background: i < 15 && isPlayingRecording ? '#22c55e' : '#d1d5db',
                    borderRadius: '2px',
                  }}
                />
              ))}
            </div>
            <span style={viewStyles.recordingDuration}>{formatDuration(call.duration)}</span>
          </div>
        </DetailSection>
      )}

      {/* Notes */}
      {call.notes && (
        <DetailSection title={t('calls.notes')}>
          <div style={viewStyles.notesBox}>{call.notes}</div>
        </DetailSection>
      )}

      {/* Quick Actions */}
      <DetailSection title={t('calls.quickActions')}>
        <div style={viewStyles.actionsGrid}>
          {onCallback && (
            <button onClick={() => onCallback(call)} style={viewStyles.quickActionBtn}>
              <Phone size={18} />
              <span>{t('calls.callback')}</span>
            </button>
          )}
          {onSendMessage && (
            <button onClick={() => onSendMessage(call)} style={viewStyles.quickActionBtn}>
              <MessageSquare size={18} />
              <span>{t('calls.message')}</span>
            </button>
          )}
          {onCreateTask && (
            <button onClick={() => onCreateTask(call)} style={viewStyles.quickActionBtn}>
              <ListTodo size={18} />
              <span>{t('calls.createTask')}</span>
            </button>
          )}
        </div>
      </DetailSection>

      {/* Footer */}
      <DetailActions>
        {onDelete && (
          <Button variant="danger" icon={Trash2} onClick={() => onDelete(call.id)}>
            {t('common.delete')}
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DetailActions>
    </DetailPanel>
  );
}

// =============================================================================
// CREATE MODE COMPONENT
// =============================================================================

function CallCreateMode({
  callId,
  onClose,
  onSave,
}: {
  callId?: string;
  onClose: () => void;
  onSave?: (data: CallFormData) => void;
}) {
  const { t } = useTranslation();
  const { contacts } = useHubStore();

  const [formData, setFormData] = useState<CallFormData>({
    contactId: undefined,
    contactName: '',
    phone: '',
    direction: 'out',
    duration: undefined,
    at: new Date().toISOString().slice(0, 16),
    notes: '',
    answered: true,
  });

  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const contactInputRef = useRef<HTMLInputElement>(null);

  const filteredContacts = contacts
    .filter(
      (contact) =>
        contact.displayName.toLowerCase().includes(contactSearch.toLowerCase()) ||
        (contact.handles.phone && contact.handles.phone.includes(contactSearch))
    )
    .slice(0, 5);

  const handleSelectContact = (contact: (typeof contacts)[0]) => {
    setFormData({
      ...formData,
      contactId: contact.id,
      contactName: contact.displayName,
      phone: contact.handles.phone || formData.phone,
    });
    setContactSearch(contact.displayName);
    setShowContactDropdown(false);
  };

  const handleContactInputChange = (value: string) => {
    setContactSearch(value);
    setFormData({ ...formData, contactName: value, contactId: undefined });
    setShowContactDropdown(true);
  };

  const handleSubmit = () => {
    if (onSave) {
      onSave(formData);
    }
  };

  const durationMinutes = formData.duration ? Math.floor(formData.duration / 60) : 0;
  const durationSeconds = formData.duration ? formData.duration % 60 : 0;

  const handleDurationChange = (minutes: number, seconds: number) => {
    const totalSeconds = minutes * 60 + seconds;
    setFormData({ ...formData, duration: totalSeconds > 0 ? totalSeconds : undefined });
  };

  const isEditMode = !!callId;

  return (
    <DetailPanel
      onClose={onClose}
      title={isEditMode ? t('calls.editCall') : t('calls.registerCall')}
      subtitle={t('calls.enterCallDetails')}
      icon={<Phone size={18} />}
      accentColor="#22c55e"
      width="50%"
      footer={
        <DetailActions>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditMode ? t('common.save') : t('calls.register')}
          </Button>
        </DetailActions>
      }
    >
      {/* Direction */}
      <DetailSection title={t('calls.direction')}>
        <div style={formStyles.row}>
          <button
            type="button"
            style={formStyles.directionBtn(formData.direction === 'out', '#3b82f6')}
            onClick={() => setFormData({ ...formData, direction: 'out' })}
          >
            <PhoneOutgoing size={18} />
            {t('calls.outgoing')}
          </button>
          <button
            type="button"
            style={formStyles.directionBtn(formData.direction === 'in', '#22c55e')}
            onClick={() => setFormData({ ...formData, direction: 'in' })}
          >
            <PhoneIncoming size={18} />
            {t('calls.incoming')}
          </button>
        </div>
      </DetailSection>

      {/* Contact */}
      <DetailSection title={t('calls.contact')}>
        <div style={{ position: 'relative', marginBottom: '12px' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted, #999)',
              pointerEvents: 'none',
            }}
          />
          <input
            ref={contactInputRef}
            type="text"
            value={contactSearch}
            onChange={(e) => handleContactInputChange(e.target.value)}
            onFocus={() => setShowContactDropdown(true)}
            onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
            placeholder={t('calls.searchContact')}
            style={formStyles.inputWithIcon}
          />
          {showContactDropdown && filteredContacts.length > 0 && (
            <div style={formStyles.dropdown}>
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => handleSelectContact(contact)}
                  style={formStyles.dropdownItem}
                >
                  <div style={formStyles.avatar}>{contact.displayName[0]?.toUpperCase() || '?'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{contact.displayName}</div>
                    {contact.handles.phone && (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted, #999)' }}>
                        {contact.handles.phone}
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <PhoneField
          name="phone"
          label={t('calls.phoneNumber')}
          value={formData.phone || ''}
          onChange={(v) => setFormData({ ...formData, phone: v })}
        />
      </DetailSection>

      {/* Status & Duration */}
      <DetailSection title={t('calls.callDetails')}>
        <div style={formStyles.row}>
          <div style={formStyles.halfField}>
            <label style={formStyles.label}>{t('calls.status')}</label>
            <div style={formStyles.statusBtns}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, answered: true })}
                style={{
                  ...formStyles.statusBtn,
                  background: formData.answered ? '#dcfce7' : 'transparent',
                  borderColor: formData.answered ? '#22c55e' : 'var(--line, #e5e7eb)',
                  color: formData.answered ? '#16a34a' : 'var(--text-secondary)',
                }}
              >
                <Phone size={16} />
                {t('calls.answered')}
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, answered: false, duration: undefined })}
                style={{
                  ...formStyles.statusBtn,
                  background: !formData.answered ? '#fee2e2' : 'transparent',
                  borderColor: !formData.answered ? '#ef4444' : 'var(--line, #e5e7eb)',
                  color: !formData.answered ? '#dc2626' : 'var(--text-secondary)',
                }}
              >
                <PhoneMissed size={16} />
                {formData.direction === 'in' ? t('calls.missed') : t('calls.notAnswered')}
              </button>
            </div>
          </div>
        </div>

        {formData.answered && (
          <div style={{ marginTop: '16px' }}>
            <label style={formStyles.label}>{t('calls.duration')}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                min="0"
                value={durationMinutes}
                onChange={(e) => handleDurationChange(parseInt(e.target.value) || 0, durationSeconds)}
                placeholder="0"
                style={formStyles.durationInput}
              />
              <span style={{ color: 'var(--text-muted, #999)', fontSize: '14px' }}>min</span>
              <input
                type="number"
                min="0"
                max="59"
                value={durationSeconds}
                onChange={(e) => handleDurationChange(durationMinutes, parseInt(e.target.value) || 0)}
                placeholder="0"
                style={formStyles.durationInput}
              />
              <span style={{ color: 'var(--text-muted, #999)', fontSize: '14px' }}>sec</span>
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px' }}>
          <label style={formStyles.label}>{t('calls.dateTime')}</label>
          <input
            type="datetime-local"
            value={formData.at}
            onChange={(e) => setFormData({ ...formData, at: e.target.value })}
            style={formStyles.input}
          />
        </div>
      </DetailSection>

      {/* Notes */}
      <DetailSection title={t('calls.notes')}>
        <TextAreaField
          name="notes"
          label=""
          value={formData.notes || ''}
          onChange={(v) => setFormData({ ...formData, notes: v })}
          placeholder={t('calls.topicsDiscussed')}
          rows={4}
        />
      </DetailSection>
    </DetailPanel>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CallWidget({
  isOpen = true,
  callId,
  call,
  onClose,
  onSave,
  onCallback,
  onCreateTask,
  onSendMessage,
  onDelete,
}: CallWidgetProps) {
  // If we have call data, show VIEW MODE
  const isViewMode = !!call;

  if (!isOpen) return null;

  if (isViewMode && call) {
    return (
      <CallViewMode
        call={call}
        onClose={onClose}
        onCallback={onCallback}
        onCreateTask={onCreateTask}
        onSendMessage={onSendMessage}
        onDelete={onDelete}
      />
    );
  }

  return <CallCreateMode callId={callId} onClose={onClose} onSave={onSave} />;
}

// =============================================================================
// VIEW MODE STYLES
// =============================================================================

const viewStyles = {
  contactCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px',
    background: 'linear-gradient(135deg, #f9fafb, #f3f4f6)',
    borderRadius: '16px',
    marginBottom: '20px',
  } as React.CSSProperties,
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: 600,
    flexShrink: 0,
  } as React.CSSProperties,
  contactInfo: {
    flex: 1,
  } as React.CSSProperties,
  contactName: {
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--text, #1f2937)',
    marginBottom: '4px',
  } as React.CSSProperties,
  contactPhone: {
    fontSize: '14px',
    color: 'var(--text-muted, #6b7280)',
  } as React.CSSProperties,
  directionBadge: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  } as React.CSSProperties,
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    background: 'var(--bg, #f9fafb)',
    borderRadius: '12px',
  } as React.CSSProperties,
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  } as React.CSSProperties,
  statContent: {
    flex: 1,
  } as React.CSSProperties,
  statLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted, #9ca3af)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  statValue: {
    fontSize: '15px',
    fontWeight: 600,
    color: 'var(--text, #1f2937)',
    marginTop: '2px',
  } as React.CSSProperties,
  statusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '12px',
    borderLeft: '4px solid',
    marginBottom: '20px',
  } as React.CSSProperties,
  statusIcon: {} as React.CSSProperties,
  statusText: {
    fontSize: '14px',
    color: 'var(--text, #374151)',
  } as React.CSSProperties,
  recordingPlayer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'var(--bg, #f9fafb)',
    borderRadius: '12px',
  } as React.CSSProperties,
  playBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    background: '#22c55e',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,
  waveform: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    height: '32px',
  } as React.CSSProperties,
  recordingDuration: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-muted, #6b7280)',
  } as React.CSSProperties,
  notesBox: {
    padding: '14px',
    background: 'var(--bg, #f9fafb)',
    borderRadius: '10px',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'var(--text, #374151)',
    whiteSpace: 'pre-wrap',
  } as React.CSSProperties,
  actionsGrid: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  quickActionBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '10px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-secondary, #6b7280)',
    transition: 'all 0.15s',
  } as React.CSSProperties,
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    background: '#fef2f2',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  } as React.CSSProperties,
  closeBtn: {
    padding: '10px 20px',
    border: '1px solid var(--line, #e5e7eb)',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  } as React.CSSProperties,
};

// =============================================================================
// FORM MODE STYLES
// =============================================================================

const formStyles = {
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  } as React.CSSProperties,
  inputWithIcon: {
    width: '100%',
    padding: '10px 12px 10px 40px',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  } as React.CSSProperties,
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '100px',
    fontFamily: 'inherit',
    lineHeight: 1.6,
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text, #374151)',
    marginBottom: '6px',
  } as React.CSSProperties,
  row: {
    display: 'flex',
    gap: '12px',
  } as React.CSSProperties,
  halfField: {
    flex: 1,
  } as React.CSSProperties,
  directionBtn: (selected: boolean, color: string) =>
    ({
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px',
      border: `2px solid ${selected ? color : 'var(--line, #ddd)'}`,
      borderRadius: '10px',
      cursor: 'pointer',
      background: selected ? `${color}15` : '#fff',
      fontWeight: 600,
      fontSize: '14px',
      color: selected ? color : 'var(--text-secondary)',
      transition: 'all 0.15s',
    }) as React.CSSProperties,
  statusBtns: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  statusBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 12px',
    border: '2px solid',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    transition: 'all 0.15s',
  } as React.CSSProperties,
  durationInput: {
    width: '70px',
    padding: '10px 12px',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    textAlign: 'center',
  } as React.CSSProperties,
  cancelBtn: {
    padding: '10px 20px',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  } as React.CSSProperties,
  submitBtn: {
    padding: '10px 24px',
    border: 'none',
    borderRadius: '8px',
    background: '#22c55e',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
  } as React.CSSProperties,
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: '#fff',
    border: '1px solid var(--line, #ddd)',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 10,
    maxHeight: '200px',
    overflowY: 'auto',
    marginTop: '4px',
  } as React.CSSProperties,
  dropdownItem: {
    width: '100%',
    padding: '12px',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid var(--line, #f0f0f0)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    textAlign: 'left',
  } as React.CSSProperties,
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#22c55e',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 600,
    flexShrink: 0,
  } as React.CSSProperties,
};
